import { z } from 'zod';
import { PORTOUT_NETWORKS } from './screens';

/**
 * No-code carrier connectors (owner's request, 2026-10-30): a carrier's REST API described from the
 * frontend, without a code release. A definition holds the carrier's base address, how to sign in,
 * the values each MVNO enters (its credentials), and one request template per 212Ez carrier action.
 * Templates use {{placeholders}}: the action's input (`{{sim}}`, `{{mdn}}`, ...) and the connection's
 * values (`{{values.resellerId}}`). The answer is read with simple paths (`data.phoneNumber`, or
 * `xml:mdn` for the first <mdn> of an XML answer).
 */

/** 212Ez's carrier actions, with the placeholders each one offers. */
export const CARRIER_ACTIONS = [
  {
    key: 'activate',
    label: 'Activate a SIM',
    vars: [
      'sim',
      'imei',
      'carrierCode',
      'wholesaleCode',
      'zip',
      'mdn',
      'areaCode',
      'firstName',
      'lastName',
      'address1',
      'address2',
      'city',
      'state',
    ],
  },
  { key: 'changeSim', label: 'Change SIM', vars: ['mdn', 'oldSim', 'newSim'] },
  { key: 'suspend', label: 'Suspend a line', vars: ['mdn', 'sim'] },
  { key: 'restore', label: 'Restore a line', vars: ['mdn', 'sim'] },
  { key: 'disconnect', label: 'Disconnect a line', vars: ['mdn', 'sim'] },
  { key: 'reconnect', label: 'Reconnect a line', vars: ['mdn', 'sim'] },
  { key: 'changeNumber', label: 'Change number', vars: ['mdn', 'sim', 'zip'] },
  { key: 'hotline', label: 'Hotline on/off', vars: ['mdn', 'restrict'] },
  { key: 'addTopup', label: 'Add a top-up', vars: ['mdn', 'sim', 'wholesaleCode'] },
  { key: 'changePlan', label: 'Change plan', vars: ['mdn', 'sim', 'wholesaleCode'] },
  { key: 'renewPlan', label: 'Renew the plan', vars: ['mdn', 'sim', 'wholesaleCode'] },
  { key: 'addProduct', label: 'Add a product', vars: ['mdn', 'sim', 'wholesaleCode', 'status'] },
  {
    key: 'setFeature',
    label: 'Wifi Calling on/off',
    vars: ['mdn', 'sim', 'feature', 'enabled', 'address'],
  },
  { key: 'usage', label: 'Usage of a line', vars: ['mdn', 'sim', 'from', 'to', 'type'] },
  { key: 'lineEnquiry', label: 'Line enquiry', vars: ['mdn', 'sim'] },
  { key: 'resetVoicemail', label: 'Reset voicemail password', vars: ['mdn', 'sim'] },
  { key: 'cancelDevice', label: 'Cancel device', vars: ['mdn', 'sim'] },
  { key: 'deprovisionSim', label: 'Take an unused SIM out of service', vars: ['sim', 'remark'] },
  { key: 'issueEsim', label: 'Issue an eSIM', vars: ['mdn', 'oldSim'] },
  { key: 'checkCoverage', label: 'Coverage check', vars: ['carrierCode', 'zip'] },
  { key: 'checkDevice', label: 'Device (IMEI) check', vars: ['esn'] },
  { key: 'checkPortability', label: 'Portability check', vars: ['mdn', 'carrierCode'] },
  {
    key: 'submitPortIn',
    label: 'Submit a port-in',
    vars: ['mdn', 'sim', 'carrierCode', 'accountNumber', 'pin', 'name', 'zip', 'wholesaleCode'],
  },
  { key: 'queryPortIn', label: 'Port-in status', vars: ['mdn', 'reference'] },
  { key: 'cancelPortIn', label: 'Cancel a port-in', vars: ['mdn', 'reference'] },
  { key: 'answerPortOut', label: 'Answer a port-out', vars: ['mdn', 'accept'] },
  { key: 'ocsPurchases', label: 'OCS purchases', vars: ['mdn', 'sim'] },
] as const;
export type CarrierActionKey = (typeof CARRIER_ACTIONS)[number]['key'];
const ACTION_KEYS = CARRIER_ACTIONS.map((a) => a.key) as [CarrierActionKey, ...CarrierActionKey[]];

export const CONNECTOR_AUTH_TYPES = [
  'none',
  'apiKeyHeader',
  'basic',
  'bearer',
  'oauth2Password',
  'oauth2ClientCredentials',
] as const;
export type ConnectorAuthType = (typeof CONNECTOR_AUTH_TYPES)[number];
export const AUTH_TYPE_LABELS: Record<ConnectorAuthType, string> = {
  none: 'None',
  apiKeyHeader: 'API key in a header',
  basic: 'Basic (user and password)',
  bearer: 'Bearer token',
  oauth2Password: 'OAuth2 password grant',
  oauth2ClientCredentials: 'OAuth2 client credentials',
};

/** Carrier APIs are reached over https only. */
const httpsUrl = (message: string) =>
  z
    .string()
    .trim()
    .max(300)
    .refine((v) => /^https:\/\/[^\s/]+/i.test(v), message);
const optionalHttpsUrl = (message: string) =>
  z
    .string()
    .trim()
    .max(300)
    .refine((v) => !v || /^https:\/\/[^\s/]+/i.test(v), message)
    .default('');

const fieldKey = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,39}$/, 'Letters, digits and _ only');

export const connectorFieldSchema = z.object({
  key: fieldKey,
  label: z.string().trim().min(1, 'Label is required').max(60),
  secret: z.boolean().default(false),
  required: z.boolean().default(true),
  help: z.string().trim().max(200).optional(),
});

export const actionTemplateSchema = z.object({
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
  /** Path after the base address, with placeholders: `/subscribers/{{mdn}}`. */
  path: z.string().trim().max(500),
  bodyType: z.enum(['none', 'json', 'form', 'xml']).default('json'),
  /** The body with placeholders; JSON values are escaped as JSON, XML values as XML. */
  body: z.string().max(20_000).default(''),
  /** The call succeeded when this path of the answer equals `successEquals` (and HTTP is 2xx). */
  successPath: z.string().trim().max(200).default(''),
  successEquals: z.string().trim().max(100).default(''),
  /** Where to read the new phone number, the carrier's reference, a status and an error message. */
  mdnPath: z.string().trim().max(200).default(''),
  referencePath: z.string().trim().max(200).default(''),
  statusPath: z.string().trim().max(200).default(''),
  errorPath: z.string().trim().max(200).default(''),
});
export type ActionTemplate = z.output<typeof actionTemplateSchema>;

export const customConnectorSchema = z
  .object({
    label: z.string().trim().min(1, 'Name is required').max(80),
    baseUrl: httpsUrl('Enter the https:// base address'),
    /** A sandbox base address, used by connections in Sandbox mode; empty means the same. */
    sandboxUrl: optionalHttpsUrl('Enter an https:// address or leave it empty'),
    /** Where an MVNO finds the values it enters (shown on its Connections page). */
    where: z.string().trim().max(500).default(''),
    auth: z.object({
      type: z.enum(CONNECTOR_AUTH_TYPES),
      /** Header name for apiKeyHeader (e.g. X-Api-Key). */
      headerName: z.string().trim().max(60).default(''),
      /** Token address for the OAuth2 types. */
      tokenUrl: optionalHttpsUrl('Enter the https:// token address'),
      /** Extra fields sent to the token address (client_id, audience, scope...), with placeholders. */
      tokenParams: z.record(z.string().max(500)).default({}),
      /** Which entered values sign in: e.g. user = "username", password = "password", key = "apiKey". */
      userField: z.string().trim().max(40).default(''),
      passwordField: z.string().trim().max(40).default(''),
      keyField: z.string().trim().max(40).default(''),
    }),
    fields: z.array(connectorFieldSchema).max(20),
    headers: z.record(z.string().max(500)).default({}),
    actions: z.record(z.enum(ACTION_KEYS), actionTemplateSchema).default({}),
    /**
     * What the carrier posts to the connection's callback address: where the event name and the
     * number are in the body, and which event names mean a port-in changed (212Ez then asks the
     * carrier for its status) or another provider wants the number (it joins Pending Port-out).
     */
    callbacks: z
      .object({
        eventPath: z.string().trim().max(200).default(''),
        mdnPath: z.string().trim().max(200).default(''),
        /** Event names, comma separated, case ignored. */
        portInEvents: z.string().trim().max(500).default(''),
        portOutEvents: z.string().trim().max(500).default(''),
        gainingCarrierPath: z.string().trim().max(200).default(''),
        /** The Pending Port-out list its port-outs go to. */
        portOutNetwork: z.enum(PORTOUT_NETWORKS).default(PORTOUT_NETWORKS[0]),
      })
      .default({}),
    /** The action "Test connection" runs, with these sample inputs. */
    testAction: z.enum(ACTION_KEYS).optional(),
    testInput: z.record(z.string().max(100)).default({}),
  })
  .superRefine((d, ctx) => {
    const keys = new Set(d.fields.map((f) => f.key));
    if (keys.size !== d.fields.length)
      ctx.addIssue({ code: 'custom', path: ['fields'], message: 'Each value needs its own key' });
    const a = d.auth;
    const need = (field: 'userField' | 'passwordField' | 'keyField', label: string) => {
      if (!a[field])
        ctx.addIssue({ code: 'custom', path: ['auth', field], message: `${label} is required` });
      else if (!keys.has(a[field]))
        ctx.addIssue({
          code: 'custom',
          path: ['auth', field],
          message: `No value with the key "${a[field]}"`,
        });
    };
    if (a.type === 'apiKeyHeader') {
      if (!a.headerName)
        ctx.addIssue({
          code: 'custom',
          path: ['auth', 'headerName'],
          message: 'Header is required',
        });
      need('keyField', 'Key value');
    }
    if (a.type === 'bearer') need('keyField', 'Token value');
    if (a.type === 'basic' || a.type.startsWith('oauth2')) {
      need('userField', a.type === 'oauth2ClientCredentials' ? 'Client ID value' : 'User value');
      need(
        'passwordField',
        a.type === 'oauth2ClientCredentials' ? 'Client secret value' : 'Password value',
      );
    }
    if (a.type.startsWith('oauth2') && !a.tokenUrl)
      ctx.addIssue({
        code: 'custom',
        path: ['auth', 'tokenUrl'],
        message: 'Token address is required',
      });
    const cb = d.callbacks;
    if ((cb.portInEvents || cb.portOutEvents) && (!cb.eventPath || !cb.mdnPath))
      ctx.addIssue({
        code: 'custom',
        path: ['callbacks', cb.eventPath ? 'mdnPath' : 'eventPath'],
        message: cb.eventPath ? 'Phone number path is required' : 'Event path is required',
      });
    if (d.testAction && !d.actions[d.testAction])
      ctx.addIssue({
        code: 'custom',
        path: ['testAction'],
        message: 'This action has no request yet',
      });
  });
export type CustomConnectorInput = z.input<typeof customConnectorSchema>;
export type CustomConnectorDef = z.output<typeof customConnectorSchema>;

export interface CustomConnectorDto extends CustomConnectorDef {
  id: string;
  /** The key a carrier connection names it by: "custom:<id>". */
  key: string;
  /** Offered to every company by the platform; only its own company edits it. */
  shared: boolean;
  /** Made by the company that is looking at it (so it may edit it). */
  own: boolean;
  updatedAt: string;
  updatedBy: string;
}

/** "Try a request": runs one action of a carrier's connection with sample input. */
export const connectorTrySchema = z.object({
  action: z.enum(ACTION_KEYS, { errorMap: () => ({ message: 'Select Action' }) }),
  input: z.record(z.string().max(500)).default({}),
});
export type ConnectorTryInput = z.input<typeof connectorTrySchema>;

export interface ConnectorTryDto {
  ok: boolean;
  /** What was sent, with every secret value masked. */
  request: { method: string; url: string; body: string };
  /** What came back: HTTP status and the first 4,000 characters, secrets masked. */
  httpStatus: number | null;
  answer: string;
  durationMs: number;
  /** What 212Ez read from the answer. */
  read: { reference: string; mdn: string; status: string; error: string };
}
